import type { AthenaBillingAuditWriter } from "../../observability/audit.ts";
import { billingEventDefinition } from "../../observability/events.ts";
import type { AthenaBillingTraceRecorder } from "../../observability/traces.ts";
import { runWithBillingTrace } from "../../observability/traces.ts";
import type {
	AthenaBillingAuditEntry,
	BillingReconciliationContext,
} from "../../observability/types.ts";
import {
	mintBillingAuditIdentity,
	validateBillingAuditEntry,
} from "../../observability/validate.ts";
import {
	BILLING_RECONCILIATION_RESOURCE_WEBHOOK_REGISTRATIONS,
	type BillingReconciliationLeaseStore,
	billingReconciliationLeaseTtlMs,
} from "../../reconciliation/lease.ts";
import { emitBillingAuditFailure } from "../../reconciliation/semantic-audit.ts";
import type {
	BillingProviderExecutionContext,
	BillingWebhooksPort,
} from "../../runtime/local/providers/types.ts";
import {
	billingWebhookOwnershipMarker,
	fingerprintBillingSigningSecret,
} from "../ownership.ts";
import {
	billingWebhookConfigHash,
	buildDesiredBillingWebhookState,
} from "./desired-state.ts";
import { planBillingWebhookReconciliation } from "./planner.ts";
import type { BillingWebhookRegistrationStore } from "./repository.ts";
import type { BillingWebhookSecretStore } from "../secrets/types.ts";
import type {
	BillingWebhookReconcileInput,
	BillingWebhookReconciliationPlan,
	BillingWebhookRegistrationRecord,
} from "./types.ts";

export interface ReconcileBillingWebhooksResult {
	foreignUntouched: number;
	plans: readonly BillingWebhookReconciliationPlan[];
	traceId: string;
}

export async function reconcileBillingWebhookRegistrations(input: {
	audit?: AthenaBillingAuditWriter;
	context: BillingReconciliationContext;
	dryRun?: boolean;
	execution: BillingProviderExecutionContext;
	lease?: BillingReconciliationLeaseStore;
	port?: BillingWebhooksPort;
	reconcile: BillingWebhookReconcileInput;
	registrations?: BillingWebhookRegistrationStore;
	rememberSigningSecret?: (input: {
		connectionId: string;
		secret: string;
	}) => void;
	secretStore?: BillingWebhookSecretStore;
	traces?: AthenaBillingTraceRecorder;
}): Promise<ReconcileBillingWebhooksResult> {
	const desired = buildDesiredBillingWebhookState(input.reconcile);
	const trace = input.traces?.start({
		causationId: input.context.causationId,
		connectionId: input.reconcile.connectionId,
		correlationId: input.context.correlationId,
		operation: "billing.webhook.reconcile",
		provider: input.reconcile.provider,
		traceId: input.context.traceId,
		trigger: input.context.trigger,
	});
	const ownerId = crypto.randomUUID();
	const run = async (): Promise<ReconcileBillingWebhooksResult> => {
		let leaseEpoch: number | undefined;
		if (!input.dryRun && input.lease) {
			const leasePhase = trace?.phase("lease");
			const acquired = await input.lease.acquire({
				connectionId: input.reconcile.connectionId,
				ownerId,
				resourceKind: BILLING_RECONCILIATION_RESOURCE_WEBHOOK_REGISTRATIONS,
				ttlMs: billingReconciliationLeaseTtlMs(120_000),
			});
			leasePhase?.finish();
			if (!acquired) {
				throw new Error(
					"Another reconciler holds the webhook_registrations lease.",
				);
			}
			leaseEpoch = acquired.epoch;
		}
		const fetchPhase = trace?.phase("provider_fetch");
		const listed = input.port
			? await input.port.list(input.execution)
			: { items: [] };
		fetchPhase?.finish();
		const diffPhase = trace?.phase("diff");
		const planned = planBillingWebhookReconciliation({
			actual: listed.items,
			desired,
			management: input.reconcile.management,
		});
		diffPhase?.finish();
		if (
			!input.dryRun &&
			input.reconcile.management === "automatic" &&
			input.port
		) {
			const applyPhase = trace?.phase("apply");
			if (input.lease && leaseEpoch != null) {
				const held = await input.lease.heartbeat({
					connectionId: input.reconcile.connectionId,
					epoch: leaseEpoch,
					ownerId,
					resourceKind: BILLING_RECONCILIATION_RESOURCE_WEBHOOK_REGISTRATIONS,
					ttlMs: billingReconciliationLeaseTtlMs(120_000),
				});
				if (!held) {
					throw new Error("Reconciliation lease was lost.");
				}
			}
			for (const plan of planned.plans) {
				if (plan.kind !== "next_gen" || !desired.nextGen?.enabled) {
					continue;
				}
				if (plan.action === "create") {
					const created = await input.port.create(input.execution, {
						eventTypes: desired.nextGen.eventTypes,
						idempotencyKey: crypto.randomUUID(),
						name: desired.nextGen.name,
						url: desired.nextGen.url,
					});
					if (created.signingSecret) {
						input.rememberSigningSecret?.({
							connectionId: input.reconcile.connectionId,
							secret: created.signingSecret,
						});
					}
					await persistAndAudit(input, {
						kind: "next_gen",
						providerSigningSecret: created.signingSecret,
						providerWebhookId: created.id,
						status: "active",
						url: created.url,
						eventTypes: created.eventTypes,
						event: "billing.webhook.registration.created",
					});
				} else if (
					plan.action === "update" &&
					plan.providerWebhook &&
					input.port.update
				) {
					const updated = await input.port.update(input.execution, {
						eventTypes: desired.nextGen.eventTypes,
						id: plan.providerWebhook.id,
						name: desired.nextGen.name,
						url: desired.nextGen.url,
					});
					if (updated.signingSecret) {
						input.rememberSigningSecret?.({
							connectionId: input.reconcile.connectionId,
							secret: updated.signingSecret,
						});
					}
					await persistAndAudit(input, {
						kind: "next_gen",
						previous: {
							url: plan.providerWebhook.url,
							eventTypes: plan.providerWebhook.eventTypes,
						},
						providerSigningSecret: updated.signingSecret,
						providerWebhookId: updated.id,
						status: "active",
						url: updated.url,
						eventTypes: updated.eventTypes,
						event: "billing.webhook.registration.updated",
					});
				} else if (plan.action === "conflict") {
					await persistAndAudit(input, {
						kind: "next_gen",
						providerWebhookId: plan.providerWebhook?.id ?? "unregistered",
						status: "drifted",
						url: desired.nextGen.url,
						eventTypes: desired.nextGen.eventTypes,
						event: "billing.webhook.registration.drift_detected",
					});
				}
			}
			applyPhase?.finish();
		} else if (
			!input.dryRun &&
			input.reconcile.management === "manual" &&
			planned.plans.some((plan) => plan.action === "conflict")
		) {
			const drifted = planned.plans.find((plan) => plan.action === "conflict");
			if (drifted && desired.nextGen) {
				await persistAndAudit(input, {
					kind: "next_gen",
					providerWebhookId: drifted.providerWebhook?.id ?? "unregistered",
					status: "drifted",
					url: desired.nextGen.url,
					eventTypes: desired.nextGen.eventTypes,
					event: "billing.webhook.registration.drift_detected",
				});
			}
		}
		if (!input.dryRun && desired.classic && input.registrations) {
			await input.registrations.upsert({
				configHash: billingWebhookConfigHash({
					eventTypes: [],
					kind: "classic",
					url: desired.classic.url,
				}),
				connectionId: input.reconcile.connectionId,
				environment: input.reconcile.environment,
				eventTypes: [],
				id: crypto.randomUUID(),
				kind: "classic",
				name: desired.marker,
				provider: input.reconcile.provider,
				status: "active",
				url: desired.classic.url,
			});
		}
		await trace?.success();
		return {
			foreignUntouched: planned.foreign.length,
			plans: planned.plans,
			traceId: input.context.traceId,
		};
	};
	try {
		return await (trace ? runWithBillingTrace(trace, run) : run());
	} catch (error) {
		if (input.audit) {
			try {
				await emitBillingAuditFailure({
					context: input.context,
					error,
					writer: input.audit,
				});
			} catch {
				/* persistence of the failure row must not hide the original error */
			}
		}
		await trace?.failure(error, "apply");
		throw error;
	} finally {
		if (!input.dryRun && input.lease) {
			await input.lease.release({
				connectionId: input.reconcile.connectionId,
				ownerId,
				resourceKind: BILLING_RECONCILIATION_RESOURCE_WEBHOOK_REGISTRATIONS,
			});
		}
	}
}

async function persistAndAudit(
	input: {
		audit?: AthenaBillingAuditWriter;
		context: BillingReconciliationContext;
		reconcile: BillingWebhookReconcileInput;
		registrations?: BillingWebhookRegistrationStore;
		secretStore?: BillingWebhookSecretStore;
	},
	record: {
		event: AthenaBillingAuditEntry["event"];
		eventTypes: readonly string[];
		kind: BillingWebhookRegistrationRecord["kind"];
		previous?: unknown;
		providerSigningSecret?: string;
		providerWebhookId: string;
		status: BillingWebhookRegistrationRecord["status"];
		url: string;
	},
): Promise<void> {
	let secretFingerprint: string | undefined;
	if (record.providerSigningSecret && record.providerSigningSecret.length > 0) {
		secretFingerprint = fingerprintBillingSigningSecret(
			record.providerSigningSecret,
		);
		await input.secretStore?.storeCurrent({
			connectionId: input.reconcile.connectionId,
			fingerprint: secretFingerprint,
			secret: record.providerSigningSecret,
		});
	} else if (input.secretStore) {
		const existing = await input.secretStore.resolve(
			input.reconcile.connectionId,
		);
		if (existing.current) {
			secretFingerprint = fingerprintBillingSigningSecret(existing.current);
		}
	} else if (input.registrations) {
		const existing = await input.registrations.get({
			connectionId: input.reconcile.connectionId,
			kind: record.kind,
		});
		secretFingerprint = existing?.secretFingerprint;
	}
	const row: BillingWebhookRegistrationRecord = {
		configHash: billingWebhookConfigHash({
			eventTypes: record.eventTypes,
			kind: record.kind,
			url: record.url,
		}),
		connectionId: input.reconcile.connectionId,
		environment: input.reconcile.environment,
		eventTypes: record.eventTypes,
		id: crypto.randomUUID(),
		kind: record.kind,
		name: billingWebhookConfigHash({
			eventTypes: record.eventTypes,
			kind: record.kind,
			url: record.url,
		}),
		provider: input.reconcile.provider,
		providerWebhookId: record.providerWebhookId,
		status: record.status,
		url: record.url,
		...(secretFingerprint ? { secretFingerprint } : {}),
	};
	if (input.registrations) {
		await input.registrations.upsert({
			...row,
			name: billingWebhookOwnershipMarker({
				applicationId: input.reconcile.applicationId,
				connectionId: input.reconcile.connectionId,
			}),
		});
	}
	if (!input.audit) {
		return;
	}
	const ir = billingEventDefinition(record.event);
	const entry: AthenaBillingAuditEntry = {
		actor: { kind: "system" },
		causationId: input.context.causationId,
		connectionId: input.reconcile.connectionId,
		correlationId: input.context.correlationId,
		event: record.event,
		...mintBillingAuditIdentity(),
		outcome: "success",
		previous: record.previous,
		provider: input.reconcile.provider,
		providerSubject: { id: record.providerWebhookId, kind: "webhook" },
		result: {
			eventTypes: record.eventTypes,
			status: record.status,
			url: record.url,
		},
		traceId: input.context.traceId,
	};
	validateBillingAuditEntry(entry, ir);
	await input.audit.write(entry);
}
